import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { BrandInference, SiteInspection } from '@visiward/schemas';
import { Globe, Search, ChevronDown, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Alert, Spinner } from '@/components/ui/misc';
import { Turnstile } from '@/components/Turnstile';
import { api, ApiError } from '@/lib/api';

type Step = 'form' | 'confirm' | 'running';

export function AnalysisFlow({ mode }: { mode: 'anonymous' | 'registered' }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('form');

  // Form state
  const [url, setUrl] = useState('');
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [market, setMarket] = useState('US');
  const [language, setLanguage] = useState('en');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Inference state
  const [inspection, setInspection] = useState<SiteInspection | null>(null);
  const [inference, setInference] = useState<BrandInference | null>(null);
  const [confirmedBrand, setConfirmedBrand] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const needsTurnstile = mode === 'anonymous';

  async function handleInspect(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!url.trim()) {
      setError('Please enter a website URL.');
      return;
    }
    if (needsTurnstile && !turnstileToken) {
      setError('Please complete the verification.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.inspectSite(url.trim(), turnstileToken ?? undefined);
      setInspection(res.inspection);
      setInference(res.inference);
      setConfirmedBrand(brandName.trim() || res.inference.brandName || '');
      setStep('confirm');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not inspect that website.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRun() {
    if (!inspection) return;
    setError(null);
    setStep('running');
    setBusy(true);
    try {
      const competitorList = competitors
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.createAnalysis({
        websiteUrl: inspection.canonicalUrl ?? inspection.requestedUrl,
        brandName: confirmedBrand.trim() || null,
        description: description || inference?.description || '',
        industry: inference?.industry || '',
        competitors: competitorList.length ? competitorList : inference?.competitors,
        market,
        language,
        turnstileToken: turnstileToken ?? undefined,
      });
      // Invalidate project/history caches for registered users.
      if (mode === 'registered') {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      }
      const base = mode === 'anonymous' ? '/results' : '/app/results';
      navigate(`${base}/${res.run.runId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The analysis could not be completed.');
      setStep('confirm');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'running') {
    return (
      <div className="panel flex flex-col items-center gap-4 px-6 py-16 text-center">
        <Spinner className="h-8 w-8" />
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Analyzing your visibility…</h3>
          <p className="mt-1 text-sm text-text-secondary">
            We’re generating prompts and evaluating how your brand appears in AI-generated answers.
            This can take up to a minute.
          </p>
        </div>
      </div>
    );
  }

  if (step === 'confirm' && inference && inspection) {
    const lowConfidence = inference.brandNameConfidence < 0.5;
    return (
      <div className="mx-auto max-w-2xl space-y-5">
        <div className="panel p-6">
          <div className="mb-4 flex items-center gap-2 text-accent-blue">
            <Sparkles size={18} />
            <span className="text-sm font-semibold">We identified this website as</span>
          </div>

          {lowConfidence && (
            <div className="mb-4">
              <Alert tone="info">
                We could not confidently identify the brand name. You can add it now or continue
                without one.
              </Alert>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="confirm-brand">Brand</Label>
              <Input
                id="confirm-brand"
                value={confirmedBrand}
                onChange={(e) => setConfirmedBrand(e.target.value)}
                placeholder="Add a brand name (optional)"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Category</Label>
                <div className="input-base flex items-center text-text-secondary">
                  {inference.industry || 'General'}
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <div className="input-base flex items-center gap-2 truncate text-text-secondary">
                  <Globe size={14} className="shrink-0" />
                  <span className="truncate">{inspection.domain}</span>
                </div>
              </div>
            </div>
            {inference.description && (
              <div>
                <Label>Business summary</Label>
                <p className="rounded-lg border border-border bg-bg-raised/50 p-3 text-sm text-text-secondary">
                  {inference.description}
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4">
              <Alert tone="error">{error}</Alert>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={() => setStep('form')} disabled={busy}>
              Back
            </Button>
            <Button onClick={handleRun} disabled={busy}>
              Confirm and continue
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
        <p className="text-center text-xs text-text-muted">
          Brand name is optional. You can edit it above before running the analysis.
        </p>
      </div>
    );
  }

  // Step: form
  return (
    <form onSubmit={handleInspect} className="mx-auto max-w-2xl space-y-5">
      <div className="panel p-6">
        <Label htmlFor="url">
          Website URL <span className="text-danger">*</span>
        </Label>
        <div className="relative">
          <Globe
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <Input
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="example.com"
            className="pl-9"
            autoFocus
            required
          />
        </div>
        <p className="mt-1.5 text-xs text-text-muted">
          We’ll inspect your site and infer your brand for you to confirm.
        </p>

        <div className="mt-4">
          <Label htmlFor="brand">Brand name (optional)</Label>
          <Input
            id="brand"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            placeholder="Leave blank to auto-detect"
          />
        </div>

        {/* Advanced settings (collapsed by default) */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="mt-4 flex items-center gap-1.5 text-sm font-medium text-text-secondary transition hover:text-text-primary"
        >
          <ChevronDown size={15} className={showAdvanced ? 'rotate-180 transition' : 'transition'} />
          Advanced settings
        </button>
        {showAdvanced && (
          <div className="mt-4 space-y-4 border-t border-border pt-4">
            <div>
              <Label htmlFor="desc">Short description</Label>
              <Textarea
                id="desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does your business do?"
              />
            </div>
            <div>
              <Label htmlFor="comp">Competitors (comma-separated)</Label>
              <Input
                id="comp"
                value={competitors}
                onChange={(e) => setCompetitors(e.target.value)}
                placeholder="Competitor A, Competitor B"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="market">Target market</Label>
                <Input id="market" value={market} onChange={(e) => setMarket(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="lang">Language</Label>
                <Input id="lang" value={language} onChange={(e) => setLanguage(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {needsTurnstile && <Turnstile onToken={setTurnstileToken} />}

        {error && (
          <div className="mt-4">
            <Alert tone="error">{error}</Alert>
          </div>
        )}

        <Button type="submit" className="mt-5 w-full" size="lg" disabled={busy}>
          {busy ? <Spinner /> : <Search size={18} />}
          {busy ? 'Inspecting…' : 'Inspect website'}
        </Button>
      </div>
    </form>
  );
}
